import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAllDevicesLastRecordTimes, getDeviceSetPointHistory, getDevicesUptime } from 'services/adminCenterService';

export const fetchAllDevicesLastRecordTimes = createAsyncThunk(
  'adminCenter/fetchAllDevicesLastRecordTimes',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getAllDevicesLastRecordTimes();
      return response?.resDevices || response || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch devices last record times');
    }
  },
);

export const fetchDeviceSetPointHistory = createAsyncThunk(
  'adminCenter/fetchDeviceSetPointHistory',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getDeviceSetPointHistory(params);
      return response?.rows || response || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch device set point history');
    }
  },
);

export const fetchDevicesUptime = createAsyncThunk('adminCenter/fetchDevicesUptime', async (params, { rejectWithValue }) => {
  try {
    const response = await getDevicesUptime(params);
    return response?.rows || response || [];
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to fetch devices uptime');
  }
});

const adminCenterInitialState = {
  devicesLastRecordTimes: [],
  devicesLastRecordTimesLoading: false,
  devicesLastRecordTimesError: null,
  devicesLastRecordTimesLastFetchedAt: null,
  deviceSetPointHistory: [],
  deviceSetPointHistoryLoading: false,
  deviceSetPointHistoryError: null,
  deviceSetPointHistoryLastFetchedAt: null,
  devicesUptime: [],
  devicesUptimeLoading: false,
  devicesUptimeError: null,
  devicesUptimeLastFetchedAt: null,
};

export const adminCenterSlice = createSlice({
  name: 'adminCenter',
  initialState: adminCenterInitialState,
  reducers: {
    clearDevicesLastRecordTimes: state => {
      state.devicesLastRecordTimes = [];
      state.devicesLastRecordTimesError = null;
      state.devicesLastRecordTimesLastFetchedAt = null;
    },
    clearDeviceSetPointHistory: state => {
      state.deviceSetPointHistory = [];
      state.deviceSetPointHistoryError = null;
      state.deviceSetPointHistoryLastFetchedAt = null;
    },
    clearDevicesUptime: state => {
      state.devicesUptime = [];
      state.devicesUptimeError = null;
      state.devicesUptimeLastFetchedAt = null;
    },
  },
  extraReducers: {
    [fetchAllDevicesLastRecordTimes.pending]: state => {
      state.devicesLastRecordTimesLoading = true;
      state.devicesLastRecordTimesError = null;
    },
    [fetchAllDevicesLastRecordTimes.fulfilled]: (state, action) => {
      state.devicesLastRecordTimes = Array.isArray(action.payload) ? action.payload : [];
      state.devicesLastRecordTimesLoading = false;
      state.devicesLastRecordTimesError = null;
      state.devicesLastRecordTimesLastFetchedAt = Date.now();
    },
    [fetchAllDevicesLastRecordTimes.rejected]: (state, action) => {
      state.devicesLastRecordTimesLoading = false;
      state.devicesLastRecordTimesError =
        action.payload || action.error?.message || 'Failed to fetch devices last record times';
    },
    [fetchDeviceSetPointHistory.pending]: state => {
      state.deviceSetPointHistoryLoading = true;
      state.deviceSetPointHistoryError = null;
    },
    [fetchDeviceSetPointHistory.fulfilled]: (state, action) => {
      state.deviceSetPointHistory = Array.isArray(action.payload) ? action.payload : [];
      state.deviceSetPointHistoryLoading = false;
      state.deviceSetPointHistoryError = null;
      state.deviceSetPointHistoryLastFetchedAt = Date.now();
    },
    [fetchDeviceSetPointHistory.rejected]: (state, action) => {
      state.deviceSetPointHistoryLoading = false;
      state.deviceSetPointHistoryError =
        action.payload || action.error?.message || 'Failed to fetch device set point history';
    },
    [fetchDevicesUptime.pending]: state => {
      state.devicesUptimeLoading = true;
      state.devicesUptimeError = null;
    },
    [fetchDevicesUptime.fulfilled]: (state, action) => {
      state.devicesUptime = Array.isArray(action.payload) ? action.payload : [];
      state.devicesUptimeLoading = false;
      state.devicesUptimeError = null;
      state.devicesUptimeLastFetchedAt = Date.now();
    },
    [fetchDevicesUptime.rejected]: (state, action) => {
      state.devicesUptimeLoading = false;
      state.devicesUptimeError = action.payload || action.error?.message || 'Failed to fetch devices uptime';
    },
  },
});

export const { clearDevicesLastRecordTimes, clearDeviceSetPointHistory, clearDevicesUptime } = adminCenterSlice.actions;

export default adminCenterSlice.reducer;
